import { z } from "zod";
import { PAGE_SIZE_MAX, paginationQuery, searchParam } from "../pagination";
import type { NocRef } from "./common";
import { COMPETITOR_REASON_MAX, type CompetitorItem } from "./competitors";
import type { MeetDisciplineSummary, MeetEventSummary, MeetSportSummary } from "./meets";
import { COMPETITOR_STATUSES, type CompetitorStatus, type Medal, type MemberRole } from "./vocab";
import { START_LIST_LABEL_MAX } from "./results";
import type { AthleteNumberKind } from "./start-list-profile";

/**
 * Who takes part in ONE games, read across its whole programme — OVR's
 * Participants section (slice R3):
 *
 *   GET /athletes?games_id=…           Paginated<AthleteItem>
 *   GET /athletes/:personId?games_id=… AthleteDetail
 *   GET /nocs?games_id=…               Paginated<NocItem>
 *   GET /nocs/:id?games_id=…           NocDetail
 *   GET /competitors?games_id=…        Paginated<CompetitorListItem>   teams & entries
 *
 * Read-only, `COMPETITION_READ_ROLES`. What these return is a printed name, a
 * gender, the organization, the sports, the events and the medals — no date
 * of birth, identity number, contact, photo or accreditation number.
 *
 * An ATHLETE of the games is either (user, 2026-10-28: "every approved GMS
 * athlete is in OVR, and a new one shows as soon as it is approved"):
 *  · ENTERED — on the roster (`competitor_members`, any role) of a competitor
 *    in an event of the games. Every reader sees these, NOT narrowed by the
 *    caller's group scope: start lists and results already show them; or
 *  · REGISTERED — holding an APPROVED athlete card (category `Aa`,
 *    `ATHLETE_CATEGORY_CODE`) of the games. That is GMS data, so these are
 *    narrowed by the caller's group scope like every card read: admins see
 *    all, a scoped login the cards its group covers, other roles none.
 * A registered athlete with no entry has `events_count` 0, and their NOC and
 * sports are the card's. An NOC is the organization a competitor is entered
 * for, or the card's (`responsible_organization_id`; its `code` is the
 * country's IOC code). Medals are the medal desk's (`event_medals`) on medal
 * events only — the tally's rule — never OVR's proposal.
 *
 * Every export is prefixed (athlete…, noc…, …Competitors…) because all the
 * competition modules share one barrel.
 */

// ─── Limits ──────────────────────────────────────────────────────────────────

/** Athletes and teams & entries: one screen of rows. */
export const PARTICIPANTS_PAGE_SIZE_DEFAULT = 50;
/** NOCs: a games has tens of them (11 at the SEA Games), so one page normally holds them all. */
export const NOCS_PAGE_SIZE_DEFAULT = PAGE_SIZE_MAX;
/**
 * The most entries `GET /athletes/:personId` lists. A person has a handful in
 * one games (13 at most in an 8k-meet dataset); the cap is applied in SQL,
 * and `events_count` is always the true number, so a client can tell.
 */
export const ATHLETE_ENTRIES_MAX = 100;

/** `sort` on `GET /athletes`: by printed name, or by NOC code then name. */
export const ATHLETE_SORT_KEYS = ["name", "noc"] as const;
export type AthleteSortKey = (typeof ATHLETE_SORT_KEYS)[number];

/**
 * A person's gender as the participant reads report it. `people.gender` is
 * free text today ("MALE" from older data, "M" from the demo seed); the
 * API folds M / MALE and F / FEMALE (any case) onto these two, and anything
 * else onto null.
 */
export const PERSON_GENDERS = ["MALE", "FEMALE"] as const;
export type PersonGender = (typeof PERSON_GENDERS)[number];

// ─── Fields ──────────────────────────────────────────────────────────────────

/** Lower-cased like `clientIdField`: Postgres prints uuids in lower case. */
const idField = z.string().uuid().toLowerCase();
const pageField = paginationQuery.shape.page;
const pageSizeField = (fallback: number) => z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(fallback);

// ─── Queries ─────────────────────────────────────────────────────────────────

/**
 * `GET /athletes` — the athletes of one games, entered or registered (see
 * above). The filters choose WHICH athletes (combined with AND): `sport_id` /
 * `event_id` / `responsible_organization_id` = has at least one entry there,
 * or (registered) a card of that sport / shortlisted for that event / of that
 * organization. A row always describes the athlete's whole games — every
 * sport, every entry, every medal — whatever the filter. `search` matches the
 * printed name (`CompetitorMemberRef.name`).
 */
export const listAthletesQuery = z.object({
	games_id: idField,
	search: searchParam.optional(),
	responsible_organization_id: idField.optional(),
	sport_id: idField.optional(),
	event_id: idField.optional(),
	gender: z.enum(PERSON_GENDERS).optional(),
	sort: z.enum(ATHLETE_SORT_KEYS).default("name"),
	page: pageField,
	page_size: pageSizeField(PARTICIPANTS_PAGE_SIZE_DEFAULT),
});
export type ListAthletesQuery = z.input<typeof listAthletesQuery>;

/** `GET /athletes/:personId?games_id=` — one athlete in one games. */
export const athleteDetailQuery = z.object({ games_id: idField });
export type AthleteDetailQuery = z.input<typeof athleteDetailQuery>;

/** `GET /nocs` — the organizations with at least one competitor in the games, by NOC code (no country last), then name. */
export const listNocsQuery = z.object({
	games_id: idField,
	page: pageField,
	page_size: pageSizeField(NOCS_PAGE_SIZE_DEFAULT),
});
export type ListNocsQuery = z.input<typeof listNocsQuery>;

/** `GET /nocs/:id?games_id=` — one organization in one games, per sport. */
export const nocDetailQuery = z.object({ games_id: idField });
export type NocDetailQuery = z.input<typeof nocDetailQuery>;

/**
 * `GET /competitors` — teams & entries across one games: the rows of
 * `GET /events/:id/competitors`, each with its event. Filters combine with
 * AND; `search` matches what the per-event list matches (team name, a
 * member's printed name, the organization's name, its NOC code). Ordered like
 * the programme — sport, discipline, event (each by `sort_order`, `code`) —
 * then NOC code, organization name, `entry_no`.
 */
export const listGamesCompetitorsQuery = z.object({
	games_id: idField,
	sport_id: idField.optional(),
	discipline_id: idField.optional(),
	event_id: idField.optional(),
	responsible_organization_id: idField.optional(),
	status: z.enum(COMPETITOR_STATUSES).optional(),
	search: searchParam.optional(),
	page: pageField,
	page_size: pageSizeField(PARTICIPANTS_PAGE_SIZE_DEFAULT),
});
export type ListGamesCompetitorsQuery = z.input<typeof listGamesCompetitorsQuery>;

// ─── Responses ───────────────────────────────────────────────────────────────

/** Awarded medals (`event_medals`, medal events only), counted. A team medal counts once for the team's NOC and once for each member. */
export interface MedalCounts {
	gold: number;
	silver: number;
	bronze: number;
}

/** The event a competitor is entered in, with the discipline and sport above it. */
export interface CompetitorEventRef extends MeetEventSummary {
	discipline: MeetDisciplineSummary;
	sport: MeetSportSummary;
}

/** One row of `GET /competitors`: the per-event `CompetitorItem` (roster included) plus its event. */
export interface CompetitorListItem extends CompetitorItem {
	event: CompetitorEventRef;
}

/**
 * One row of `GET /athletes`. An allow-list: what a roster already shows to
 * every OVR role, nothing from the person's registration.
 */
export interface AthleteItem {
	people_id: string;
	/** The printed name, exactly as a roster shows it (`CompetitorMemberRef.name`): "RAHMAN Aiman Hakim". */
	name: string;
	gender: PersonGender | null;
	/**
	 * The organization the athlete competes for in this games: of their
	 * entries and their visible approved card, the first by NOC code, then
	 * name (one and the same in the data today).
	 */
	noc: NocRef;
	/** Every sport the athlete is entered in, in programme order; with no entry, the card's sports ([] for an every-sport card). */
	sports: MeetSportSummary[];
	/** Entries in this games (competitors whose roster holds the person), every status. */
	events_count: number;
	medals: MedalCounts;
}

/** One entry of an athlete. */
export interface AthleteEntry {
	competitor_id: string;
	entry_no: number;
	status: CompetitorStatus;
	/** The team's DISPLAY name (`CompetitorRef.name`) when the event is a TEAM event; null for an individual entry. */
	team_name: string | null;
	/** The person's role on that roster, and their bib there. */
	role: MemberRole;
	bib: string | null;
	noc: NocRef;
	event: CompetitorEventRef;
	/** The awarded medal of this entry (medal events only); null when none. */
	medal: Medal | null;
}

/** One awarded medal of an athlete. */
export interface AthleteMedal {
	competitor_id: string;
	medal: Medal;
	awarded_at: string;
	event: CompetitorEventRef;
}

/**
 * `GET /athletes/:personId?games_id=` — the list row, plus the entries (in
 * programme order, at most `ATHLETE_ENTRIES_MAX`) and the awarded medals
 * (GOLD, SILVER, BRONZE, then by `awarded_at`). The list's `medals` counts
 * stay; the medals themselves are `awarded_medals`.
 */
export interface AthleteDetail extends AthleteItem {
	games_id: string;
	entries: AthleteEntry[];
	awarded_medals: AthleteMedal[];
}

/**
 * One row of `GET /nocs` — every organization with an entry or a registered
 * athlete the caller can see. `entries_count` counts competitors (every
 * status); `athletes_count` counts distinct athletes, entered or registered.
 */
export interface NocItem extends NocRef {
	athletes_count: number;
	entries_count: number;
	medals: MedalCounts;
}

/** One sport of an NOC. A person in two sports counts in each; the NOC's own `athletes_count` counts them once. */
export interface NocSportSummary {
	sport: MeetSportSummary;
	entries_count: number;
	athletes_count: number;
	medals: MedalCounts;
}

/**
 * `GET /nocs/:id?games_id=` — the list row plus one summary per sport the
 * organization has entries or registered athletes in (a card's sports; an
 * every-sport card counts in no sport), in programme order (bounded by the games'
 * sports — tens). An organization with no entry in the games answers zeros
 * and `sports: []`. Its entries are `GET /competitors?responsible_organization_id=`,
 * its medals `GET /medals?responsible_organization_id=`.
 */
export interface NocDetail extends NocItem {
	games_id: string;
	sports: NocSportSummary[];
}

// ─── Athlete bibs (0079) ─────────────────────────────────────────────────────

/**
 * An athlete's bib in one sport (user, 2026-11-02): typed once on OVR's
 * athlete page, and every entry of that sport shows it — after the entry's
 * own bib and the roster member's, before nothing (`athleteBibSql`). A start
 * list's slot bib still wins for that meet (contracts `slotBib`).
 *
 *   GET /athletes/:personId/bibs?games_id=   AthleteBibs
 *   PUT /athletes/:personId/bibs/:sportId    { bib, reason? } → AthleteBibRow
 *
 * The word and the uniqueness come from the sport's start list profile
 * (`athleteNumberFor`, 2026-11-03): a Bib is held once in the whole sport, a
 * Shirt / Cap No. once per NOC — every team may wear a #10. A taken number is
 * 409 `ATHLETE_BIB_TAKEN`, with the holder's name in `details.holder`.
 * Rosters and line-ups of the sport fall back to it too (`memberBibSql`). Written by admins, and by a group
 * holding `competition.entries` for the sport (`writable`).
 */

/** The same ceiling as a start list's bib: the fallback lands in that column. */
export const ATHLETE_BIB_MAX = START_LIST_LABEL_MAX;

export const athleteBibsQuery = z.object({ games_id: idField });
export type AthleteBibsQuery = z.input<typeof athleteBibsQuery>;

/** `bib` trimmed; empty or null clears it. */
export const putAthleteBibBody = z.object({
	bib: z
		.string()
		.trim()
		.max(ATHLETE_BIB_MAX)
		.nullable()
		.transform((v) => (v === null || v === "" ? null : v)),
	reason: z.string().trim().min(1).max(COMPETITOR_REASON_MAX).optional(),
});
export type PutAthleteBibBody = z.input<typeof putAthleteBibBody>;

/** One sport of the athlete: their bib there (null = none), and whether the caller may change it. */
export interface AthleteBibRow {
	sport: MeetSportSummary;
	bib: string | null;
	writable: boolean;
	/** What the number is called in this sport (Bib, Shirt No.…); null = the athlete's events here take no number. */
	number: AthleteNumberKind | null;
	/** SPORT = one holder in the whole sport; NOC = one holder per NOC. Null with `number`. */
	unique: "SPORT" | "NOC" | null;
}

/** Every sport the athlete is entered in, in programme order, plus any sport already holding their bib. */
export interface AthleteBibs {
	people_id: string;
	games_id: string;
	sports: AthleteBibRow[];
}
